import { useCallback, useMemo, useState } from "react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { LoadingModal } from "../../../../components/LoadingModal";
import { PlusTag } from "../../../../components/PlusTag";
import { ScreenHeader } from "../../../../components/SubHeader";
import constants from "../../../../constants";
import { useQueryUrl } from "../../../../hooks/QueryUrl";
import PlusIcon from "../../../../icons/Plus";
import { Site } from "../../../../model/site";
import { api } from "../../../../services/api";
import { SiteThumbnail } from "../../../Sites/components/SiteThumbnail";
import { ItemSelection } from "../../components/ItemSelection";
import { PaymentInfo } from "../../components/PaymentInfo";
import { YEARLY_SAVING } from "../../pricing";
import { StickyBar } from "../Features/styles";
import { Container, List, NewIcon, PlanSummary, Section } from "./styles";

type SelectionItem = {
  id: string;
  title: string;
  active: boolean;
  /** The Keepo being converted (absent for "create a new Keepo"). */
  site?: Site;
};

export const CREATE_NEW_KEEPO = "CREATE_NEW_KEEPO";

export const SiteSelection = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const query = useQueryUrl();

  const selectedSite = query.get("site");

  const [isLoading, setLoading] = useState(true);
  const [items, setItems] = useState<SelectionItem[]>([]);

  const canContinue = useMemo(() => items.some(item => item.active), [items]);

  const plan = useMemo(() => (query.get("plan") as "ANNUALLY" | "MONTHLY") || "ANNUALLY", [query]);

  const selectHandler = (item: SelectionItem) => {
    const index = items.findIndex(currentItem => currentItem.id === item.id);
    const copyItems = [...items];
    copyItems.forEach(item => (item.active = false));
    copyItems[index] = { ...copyItems[index], active: true };
    setItems(copyItems);
  };

  // On to Keepo's own checkout screen. With no Keepo to pick it stands in for this step, so it replaces it
  const callCheckoutHandler = useCallback(
    (siteId?: string, replace = false) => {
      const params = new URLSearchParams({ plan });
      if (siteId && siteId !== CREATE_NEW_KEEPO) params.set("site", siteId);
      navigate(`/subscription/checkout?${params}`, { replace });
    },
    [navigate, plan]
  );

  const continueHandler = useCallback(() => {
    if (!canContinue) {
      return;
    }
    callCheckoutHandler(items.find(({ active }) => active)?.id);
  }, [canContinue, items, callCheckoutHandler]);

  const loadSites = useCallback(async () => {
    const sites = await api.getSites();

    const items: SelectionItem[] = sites
      .filter(site => (site.subscription?.type !== "PLUS" || site.subscription?.canceled) && !!site.site)
      .map(site => ({
        id: site.siteId,
        title: site.site.title,
        active: site.siteId === selectedSite,
        site: site.site,
      }));

    if (!items.length) {
      callCheckoutHandler(undefined, true);
      return;
    }

    // A single Keepo to turn PLUS: picked already, one tap away from checkout
    if (items.length === 1) {
      items[0].active = true;
    }

    // Converting an existing Keepo is the usual case: creating a new one comes last
    items.push({
      id: CREATE_NEW_KEEPO,
      active: false,
      title: t("pages.subscription.siteSelection.createNewKeepoItem"),
    });

    setItems(items);
    setLoading(false);
  }, [t, selectedSite, callCheckoutHandler]);

  useEffect(() => {
    loadSites();
  }, [loadSites]);

  const isYearly = plan === "ANNUALLY";
  const price = isYearly ? constants.price.year.value : constants.price.month.value;
  const perPeriod = isYearly
    ? t("pages.subscription.features.plans.perYear")
    : t("pages.subscription.features.plans.perMonth");

  return isLoading ? (
    <LoadingModal />
  ) : (
    <Container>
      <ScreenHeader
        title={t("pages.subscription.header.title")}
        back={{ label: t("components.appShell.nav.plus"), to: "/subscription/features" }}
      />

      <Section>
        <PlanSummary>
          <PlusTag>✦ Keepo Plus</PlusTag>
          <p className="plan">
            <strong>{t(`pages.subscription.features.plans.${isYearly ? "annually" : "monthly"}.title`)}</strong> ·{" "}
            {t(`pages.subscription.features.plans.${isYearly ? "annually" : "monthly"}.description`)}
          </p>
        </PlanSummary>
      </Section>

      <Section>
        <p className="help">{t("pages.subscription.siteSelection.help")}</p>
        <List role="radiogroup">
          {items.map((item, index) =>
            item.site ? (
              <ItemSelection
                tabIndex={index + 1}
                key={item.id}
                active={item.active}
                title={item.title}
                subtitle={`${item.site.url}.${constants.nameSite}`}
                leading={<SiteThumbnail site={item.site} />}
                onClick={() => selectHandler(item)}
              />
            ) : (
              <ItemSelection
                tabIndex={index + 1}
                key={item.id}
                dashed
                active={item.active}
                title={item.title}
                leading={
                  <NewIcon>
                    <PlusIcon width={14} height={14} color="currentColor" />
                  </NewIcon>
                }
                onClick={() => selectHandler(item)}
              />
            )
          )}
        </List>
        <PaymentInfo />
      </Section>

      {/* Same subscribe bar as the plans screen */}
      <StickyBar>
        <span className="summary">
          <span className="plan">
            {isYearly
              ? t("pages.subscription.features.page.bar.yearly", { value: YEARLY_SAVING })
              : t("pages.subscription.features.page.bar.monthly")}
          </span>
          <span className="price">
            {price}
            <small>{perPeriod}</small>
          </span>
        </span>
        <button
          type="button"
          className="plus-cta"
          onClick={continueHandler}
          tabIndex={items.length + 1}
          disabled={!canContinue}>
          {t("pages.subscription.continueButton")}
          <span className="arrow" aria-hidden="true">
            →
          </span>
        </button>
      </StickyBar>
    </Container>
  );
};
